import React, { useEffect, useRef } from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components';

import Config from '~/Config';
import { acceptedVideoFormats } from '~/utils/video';
import cx from 'classnames';

export type DragAndDropFileSelectorProps = {
  onFileUpload: (files: File[]) => void;
  disabledDragAndDrop?: boolean;
  title?: React.ReactNode;
  iconClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  descriptionClassName?: string;
  browseButtonClassName?: string;
  className?: string;
};

export default function DragAndDropFileSelector({
  onFileUpload,
  disabledDragAndDrop = false,
  title = 'Drop files here to upload',
  iconClassName,
  titleClassName,
  subtitleClassName,
  descriptionClassName,
  browseButtonClassName,
  className,
}: DragAndDropFileSelectorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLElement>();

  const onBrowseFilesClick = () => inputRef.current?.click();

  const handleFileUpload = (fileList: FileList) => {
    const files = Array.from(fileList);
    onFileUpload(files);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      handleFileUpload(fileList);
    }
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const fileList = event.dataTransfer?.files;

    if (fileList) {
      handleFileUpload(fileList);
    }
  };

  useEffect(() => {
    if (disabledDragAndDrop) return;

    const ref = (panelRef.current ?? document) as HTMLElement;
    ref.addEventListener('drop', handleDrop);

    return () => {
      panelRef.current?.removeEventListener('drop', handleDrop);
      document.removeEventListener('drop', handleDrop);
    };
  }, [panelRef.current]);

  return (
    <div
      className={cx(
        className,
        'flex flex-col items-center v2-text-neutral-500'
      )}
    >
      <i className={cx(iconClassName, 'icon icon-cloud-upload icon-xl !v2-bg-neutral-500')} />
      <div className={cx(titleClassName, 'text-xl mt-5 mb-3.5')}>{title}</div>
      <p className={cx(subtitleClassName, 'font-bold text-base mb-5')}>
        Or{' '}
        <Button
          className={cx(
            browseButtonClassName,
            '!p-0 text-base !v2-text-neutral-500 hover:!v2-text-neutral-900 underline'
          )}
          variant={ButtonVariant.Icon}
          onClick={onBrowseFilesClick}
        >
          browse
        </Button>{' '}
        files
      </p>
      <span className={cx(descriptionClassName, 'text-sm')}>
        MP4 &amp; WebM files only, max upload file size is{' '}
        {Config.FILE_UPLOAD_MAX_SIZE_MB}MB
      </span>
      <input
        ref={inputRef}
        type='file'
        className='absolute hidden'
        accept={acceptedVideoFormats.join(',')}
        multiple
        onChange={handleFileSelect}
      />
    </div>
  );
}
